/**
 * applyUpdate — the single apply path for a detected new version.
 *
 * Extracted from UpdateAvailableToast's handleReload (the toast was replaced
 * by the headless AutoUpdater, see AutoUpdater.tsx) so the silent auto-apply
 * scheduler and any future caller run the identical flush.
 *
 * This is a ROUTINE apply, so it runs applyWaitingUpdate() (reload.ts) — plain
 * SKIP_WAITING, NO cache purge. The hard purge (purgeCachesAndReload) is
 * reserved for the stale-chunk RESCUE path (stale-chunk-rescue.ts); purging on
 * a routine apply strands the newly-installed SW with an empty precache (see the
 * WHY-NO-PURGE note in reload.ts). The flush algorithm:
 *
 *   1. Post SKIP_WAITING to the active SW controller and any waiting/installing
 *      registration so the waiting worker activates.
 *   2. Race a controllerchange event against a 1500 ms timeout.
 *   3. Navigate via location.replace() with a unique _v= param so iOS Safari
 *      treats it as a fresh document request rather than a bfcache restore.
 *
 * Rule 8: every failure path inside the flush is console.error-logged, and
 * the final navigation always fires — the user is never left stuck.
 *
 * The `updateServiceWorker` callback (from vite-plugin-pwa's useRegisterSW)
 * is forwarded ONLY when `needRefresh` is set — same gating as the toast's
 * handleReload: the helper is only meaningful when the SW-based detection
 * path fired (a waiting SW exists for it to message).
 */

import { applyWaitingUpdate } from "./reload";

// ─── Self-initiated-apply flag ────────────────────────────────────────────────
//
// Set BEFORE the flush starts (synchronously, ahead of any await) so a
// controllerchange listener can distinguish "this tab initiated the SW
// activation" (applyUpdate will navigate via location.replace — do nothing)
// from "another tab activated the SW" (this tab must reload itself or it
// keeps running an old bundle against a new precache).

let applying = false;

/** True once this tab has started applying an update. */
export function isApplyingUpdate(): boolean {
  return applying;
}

/**
 * Reset the applying flag.
 *
 * @internal — test-only export. In a real session the flag stays set because
 *   applyUpdate always ends in a navigation away from the current document.
 */
export function resetIsApplyingUpdateForTesting(): void {
  applying = false;
}

// ─── Public API ───────────────────────────────────────────────────────────────

export interface ApplyUpdateOptions {
  /**
   * vite-plugin-pwa helper from useRegisterSW(). Forwarded to the flush only
   * when `needRefresh` is true (it posts SKIP_WAITING to the waiting SW on
   * its own side, alongside our direct postMessage).
   */
  updateServiceWorker?: (reloadPage?: boolean) => Promise<void>;
  /** SW-based needRefresh flag from useRegisterSW(). */
  needRefresh?: boolean;
}

/**
 * Apply a pending update: mark this tab as the initiator, then run the
 * multi-layer flush. The returned promise resolves just before navigation;
 * callers fire-and-forget (`void applyUpdate(...)`).
 */
export async function applyUpdate(opts: ApplyUpdateOptions = {}): Promise<void> {
  applying = true;
  await applyWaitingUpdate(opts.needRefresh ? opts.updateServiceWorker : undefined);
}
